import { Table, Tag } from "@caprikorn/dynamo-ui";
import type { FetchDataProps } from "@caprikorn/dynamo-ui/lib/components/dataDisplay/Table/Table.types";
import { type FC, useState } from "react";

import { useSelectedPerimeters } from "@contexts/SelectedPerimeters/SelectedPerimeters.context";
import { LayerTriple } from "@icons";
import type { Customer } from "@services/apiEcms/apiEcms.types";
import { useGetCustomers } from "@services/apiEcms/useGetCustomers";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import { usePerimeterCustomersTableColumns } from "./PerimeterCustomersTable.columns";
import classes from "./PerimeterCustomersTable.module.css";

const initialFilters: FetchDataProps = {
  pageIndex: 0,
  pageSize: 10,
  globalFilter: "",
  columnFilters: [],
  sorting: [],
};

const getRowId = (row: Customer) => row.customerId;

export const PerimeterCustomersTable: FC = () => {
  const { t } = useCustomTranslation("main");
  const { setSelectedPerimeters } = useSelectedPerimeters();

  const [filters, setFilters] = useState<FetchDataProps>(initialFilters);

  const { isLoading, data, isError } = useGetCustomers(
    filters.pageIndex + 1,
    filters.globalFilter,
  );
  const columns = usePerimeterCustomersTableColumns();

  return (
    <div className={classes.container}>
      {Boolean(data?.paging?.totalElements) && (
        <Tag
          icon={LayerTriple}
          iconStyle="start"
          label={t("perimeter.customers.countLabel", {
            count: data?.paging.totalElements,
          })}
          variant="primary"
        />
      )}

      <Table
        columns={columns}
        data={data?.customers || []}
        data-testid="PerimeterCustomersTableProvider-table"
        fetchData={setFilters}
        getRowId={getRowId}
        hasRowSelection
        isError={isError}
        isLoading={isLoading}
        onSelectionChange={(rows) => {
          setSelectedPerimeters(rows.map((row) => row.original));
        }}
        totalCount={data?.paging.totalElements || 0}
      >
        <Table.ActionBar>
          <Table.ActionBar.FilterBar
            hasGlobalFilter
            searchPlaceholder={t("perimeter.customers.searchPlaceholder")}
          />
        </Table.ActionBar>
        <Table.Content />
        <Table.Pagination />
      </Table>
    </div>
  );
};
